import { CreateAgendaItemDialog } from "@/components/agendaItems/CreateAgendaItemDialog";
import { RoleProtected } from "@/components/common/RoleProtected";
import { CreateDocumentDialog } from "@/components/documents";
import { DocumentsTable } from "@/components/documents/DocumentsTable";
import { ExportDocumentDialog } from "@/components/documents/ExportDocumentDialog";
import { PageHeader } from "@/components/page";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";
import { useRoleProtected } from "@/hooks/useRoleProtected";
import type { DocumentDto } from "@/repositories/documentRepo";
import { useUserStore } from "@/stores/userStore";
import type { Document } from "@repo/types";
import { DataTableWrapper } from "@repo/ui/components/Base/DataTable";
import {
  dataTableReverseContext,
  getAllTablesData,
  getAllTableVisibleFields,
} from "@repo/ui/components/Base/dataTableReverseContext";
import { useConstComponent } from "@repo/ui/components/Base/hooks/useNestedComponent";
import { useState } from "react";
import { useParams } from "react-router-dom";

export const DocumentsPage = () => {
  const { tenantId } = useParams();
  const activeTeam = useUserStore((s) => s.activeTeam);
  useRoleProtected("submit");
  const { loaded: tenantLoaded, data: tenantData } = useGetTenantByFriendly(
    tenantId ?? ""
  );

  const [agendaItemOpen, setAgendaItemOpen] = useState(false);
  const [documentToAdd, setDocumentToAdd] = useState<DocumentDto>();

  const tableContext = dataTableReverseContext.useAsParent();
  const Provider = useConstComponent(tableContext.Provider);
  const getDocuments = () => getAllTablesData<Document>(tableContext);
  const getSelectedFields = () => getAllTableVisibleFields(tableContext);

  return (
    <div className="flex flex-col flex-grow">
      <PageHeader
        title={
          tenantData?.name ? `${tenantData.name}'s Documents` : "Documents"
        }
      >
        {activeTeam === "org" && (
          <p className="text-red-500 w-0 flex-grow-[3] mr-8 text-balance text-right">
            You must select a team to be able to edit and create documents, and
            access linked items.
          </p>
        )}
        <div className="flex flex-row gap-3">
          <RoleProtected minRole="edit">
            <ExportDocumentDialog
              getDocuments={getDocuments}
              getPreselectedFields={getSelectedFields}
            />
          </RoleProtected>
          {tenantData?.id && (
            <RoleProtected minRole="submit">
              <CreateAgendaItemDialog
                tenantId={tenantData.id}
                disabled={activeTeam === "org"}
                open={agendaItemOpen}
                setOpen={setAgendaItemOpen}
                initialData={
                  agendaItemOpen
                    ? {
                        documentIds: [documentToAdd!.id],
                        documents: [documentToAdd!],
                      }
                    : undefined
                }
                addAnother={false}
              />
              <CreateDocumentDialog
                tenantId={tenantData.id}
                disabled={activeTeam === "org"}
                onAddToItem={(document) => {
                  setDocumentToAdd(document);
                  setAgendaItemOpen(true);
                }}
              />
            </RoleProtected>
          )}
        </div>
      </PageHeader>

      {!tenantLoaded && <div>Loading ... </div>}

      {tenantLoaded && tenantData?.id && (
        <DataTableWrapper>
          <Provider>
            <DocumentsTable tenantId={tenantData?.id} />
          </Provider>
        </DataTableWrapper>
      )}
    </div>
  );
};
